import {
  KeyboardEvent,
  KeyboardEventHandler,
  Ref,
  RefObject,
  useMemo,
  useRef,
} from "react"

export interface ListSearchInputProps {
  ref: Ref<HTMLInputElement>
  onKeyDown: KeyboardEventHandler<HTMLInputElement>
}

export interface ListKeyboardNavigation<C extends HTMLElement = HTMLElement> {
  searchInputRef: RefObject<HTMLInputElement | null>
  listRef: RefObject<C | null>
}

export interface UseListKeyboardNavigationRes<C extends HTMLElement> {
  searchInputProps: ListSearchInputProps
  keyboardNavigation: ListKeyboardNavigation<C>
}

// useListKeyboardNavigation connects a search input to the focusable rows of
// the list it filters. Spread searchInputProps onto the search input so
// ArrowDown moves focus to the first row under listRef; give each row a
// tabindex and an onKeyDown that calls handleListRowKeyDown. Rows are the
// elements with a tabindex attribute and MUST be siblings of one another:
// arrow traversal walks sibling elements, so a row nested in its own wrapper
// is unreachable (only the search input's first-row lookup searches deeper).
export function useListKeyboardNavigation<
  C extends HTMLElement,
>(): UseListKeyboardNavigationRes<C> {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<C>(null)

  return useMemo(
    () => ({
      searchInputProps: {
        ref: searchInputRef,
        onKeyDown: (e) => {
          if (e.key !== "ArrowDown") return
          const firstRow =
            listRef.current?.querySelector<HTMLElement>("[tabindex]")
          if (!firstRow) return
          e.preventDefault()
          focusListRow(firstRow)
        },
      },
      keyboardNavigation: { searchInputRef, listRef },
    }),
    []
  )
}

export interface ListRowKeyDownOptions {
  // Set false for rows with no click handler so Enter does not dispatch a
  // click that bubbles past them.
  isClickable?: boolean
  onSpace?: () => void
}

// handleListRowKeyDown implements key handling for a focused row: arrow keys
// move focus between rows (ArrowUp past the first row returns to the search
// input), Enter re-dispatches a click on the row, Space runs onSpace, and
// Escape or typing a character returns to the search input.
export function handleListRowKeyDown(
  e: KeyboardEvent<HTMLElement>,
  searchInputRef: RefObject<HTMLInputElement | null>,
  { isClickable = true, onSpace }: ListRowKeyDownOptions = {}
) {
  if (e.target !== e.currentTarget) return
  switch (e.key) {
    case "ArrowDown": {
      e.preventDefault()
      const next = siblingRow(e.currentTarget, "next")
      if (next) focusListRow(next)
      break
    }
    case "ArrowUp": {
      e.preventDefault()
      const prev = siblingRow(e.currentTarget, "previous")
      if (prev) {
        focusListRow(prev)
      } else {
        searchInputRef.current?.focus()
      }
      break
    }
    case "Enter": {
      if (!isClickable) return
      e.preventDefault()
      // Re-dispatch as a click so the row's normal click handling applies,
      // including cmd/ctrl+Enter opening href rows in a new tab.
      e.currentTarget.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
        })
      )
      break
    }
    case " ": {
      if (!onSpace) return
      e.preventDefault()
      onSpace()
      break
    }
    case "Escape": {
      // Not prevented or stopped, so an enclosing modal still closes.
      searchInputRef.current?.focus()
      break
    }
    default: {
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return
      const input = searchInputRef.current
      if (!input) return
      input.focus()
      // Focusing selects the whole query; move the cursor to the end so the
      // typed character adds to it instead of replacing it.
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }
}

// Rows can render companion elements (like expanded accordion detail rows)
// between them, so navigation skips siblings without a tabindex.
function siblingRow(
  from: Element,
  dir: "next" | "previous"
): HTMLElement | null {
  let el =
    dir === "next" ? from.nextElementSibling : from.previousElementSibling
  while (el) {
    if (el instanceof HTMLElement && el.hasAttribute("tabindex")) return el
    el = dir === "next" ? el.nextElementSibling : el.previousElementSibling
  }
  return null
}

function focusListRow(row: HTMLElement) {
  // Scroll clear of a sticky table header when focus moves up past it.
  const headerHeight = row.closest("table")?.tHead?.offsetHeight ?? 0
  row.style.scrollMarginTop = `${headerHeight}px`
  row.focus({ preventScroll: true })
  row.scrollIntoView({ block: "nearest" })
}
